import {Animated, StyleSheet} from 'react-native';
import {H5Text, H6Text} from '@buncha/components/core/text';
import React, {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  RescheduleOptionNode,
  RescheduleOptionSectionNode,
  RescheduleTimeSlots,
} from '@buncha/B2B/types/reschedule';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {Box} from '@buncha/components/core/view';
import {CustomRadio} from '@buncha/components/core/input/choice';
import RescheduleRunSelectNode from '../RescheduleRunSelectNode';

interface Props {
  header: string;
  value: number | undefined;
  data: RescheduleOptionSectionNode | undefined;
  handleOnPress: (val: number) => void;
  expandTrigger?: boolean;
  disabled?: boolean;
}

const RescheduleRunTimeSelect = (props: Props) => {
  const muli = useFontTheme('muli');
  const theme = useColorThemeType();
  const {header, value, data, handleOnPress, expandTrigger, disabled} = props;
  const animatedMaxHeight = useRef(new Animated.Value(0)).current;

  const maxHeight = useMemo(() => {
    const defaultHeight = 1000;
    const tilesPerDefaultHeight = 15;

    if (!data) return defaultHeight;

    let totalDataLength = 0;
    Object.keys(data).forEach(key => {
      totalDataLength += data?.[key as RescheduleTimeSlots]?.length;
    });

    return Math.max(
      defaultHeight,
      (defaultHeight * totalDataLength) / tilesPerDefaultHeight,
    );
  }, [data]);

  const toggleMaxHeight = useCallback(() => {
    Animated.timing(animatedMaxHeight, {
      toValue: maxHeight,
      duration: 500,
      useNativeDriver: true,
    }).start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxHeight]);

  const renderCellNode = useCallback(
    (node: RescheduleOptionNode) => {
      return (
        <RescheduleRunSelectNode
          node={node}
          handleOnPress={handleOnPress}
          disabled={disabled}
        />
      );
    },
    [handleOnPress, disabled],
  );

  useEffect(() => {
    if (expandTrigger) toggleMaxHeight();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandTrigger]);

  useEffect(() => {
    if (!data) return;
    let totalItems = 0;
    let selectedRun: RescheduleOptionNode | undefined;
    Object.keys(data)?.forEach(item => {
      data[item as RescheduleTimeSlots].forEach(run => {
        selectedRun = run;
        totalItems++;
      });
    });

    if (totalItems === 1 && selectedRun) handleOnPress(selectedRun?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <Box
      style={[
        styles.parent,
        {
          borderColor: theme?.background.gray[200],
        },
      ]}>
      <H5Text
        style={[
          styles.header,
          {
            fontFamily: muli[700].normal,
            color: theme?.text.blue[300],
          },
        ]}>
        {header}
      </H5Text>
      <Animated.View
        style={[
          styles.radioParent,
          {
            maxHeight: animatedMaxHeight,
          },
        ]}>
        {data &&
          Object.keys(data)?.map?.(section => {
            const curr = data[section as RescheduleTimeSlots];
            if (!curr?.length) return <></>;
            return (
              <Fragment>
                <H6Text
                  style={[
                    styles.nodeSectionHeader,
                    {
                      fontFamily: muli[700].normal,
                      color: theme?.text.blue[300],
                    },
                  ]}>
                  {section}
                </H6Text>
                <CustomRadio
                  selectedValue={value}
                  cellNode={renderCellNode}
                  data={curr}
                  setValue={handleOnPress}
                  containerStyle={[
                    styles.radioContainer,
                    {
                      borderColor: theme?.text.gray[200],
                    },
                  ]}
                  selectedContainerStyle={{
                    backgroundColor: theme?.text.blue[400],
                  }}
                  radioStyle={{borderColor: theme?.text?.gray?.[100]}}
                  selectedCircleStyle={{
                    borderColor: theme?.text?.gray?.[100],
                  }}
                  selectedSmallCircleStyle={{
                    backgroundColor: theme?.text?.blue?.[300],
                  }}
                />
              </Fragment>
            );
          })}
      </Animated.View>
    </Box>
  );
};

export default memo(RescheduleRunTimeSelect);

const styles = StyleSheet.create({
  parent: {
    marginTop: 20,
    borderWidth: 2,
    borderRadius: 7,
    padding: 12,
    paddingBottom: 4,
  },
  header: {
    textAlign: 'left',
    paddingBottom: 8,
  },
  radioParent: {
    overflow: 'hidden',
  },
  radioContainer: {
    borderWidth: 1,
    borderRadius: 7,
    marginVertical: 8,
    paddingLeft: 12,
  },
  nodeTouchable: {flex: 1, padding: 12},
  nodeText: {textAlign: 'left'},
  nodeTextSubtitle: {textAlign: 'left', marginLeft: 12},
  nodeSectionHeader: {
    marginBottom: 2,
    textAlign: 'left',
  },
});
